import * as z from 'zod';

import { zodDateOrString } from '../../utils';

/**
 * #### Fannie Mae LAR 83 Report: Payment/Interest Rate Change Record
 *
 * The LAR 83 report is an 80-character fixed length string. Character lengths and positions are noted at the individual fields.
 *
 * From Fannie's docs:
 *
 * The Transaction Type 83 is used to update Fannie Mae’s loan records of changes to the rate and/or payment
 * for a mortgage loan. Changes to the rate or payment that must be reported include:
 * - Scheduled Interest Rate Change/Payment Changes
 * - Scheduled Interest Rate Changes Only
 * - Scheduled Payment Changes Only
 * - ARM to Fixed Rate Loan Conversions
 * - Borrower Requested Payment Recast (excludes I/O loans in Interest Only period)
 * 
 * Here are the requirements for various events:
 * - **Scheduled Interest Rate Change / Payment Change**: Report the Transaction Type 83 to Fannie Mae prior to 8 PM eastern
 *   standard time on the 5th Business Day after the Scheduled Interest Rate Calculation Date (Rate Effective Date minus the
 *   Look Back Period.)
 * - **Scheduled Interest Rate Change Only**: Report the Transaction Type 83 to Fannie Mae prior to 8 PM eastern standard time
 *   on the 5th Business Day after the Scheduled Interest Rate Calculation Date (Rate Effective Date minus the Look Back Period).
 * - **Scheduled Payment Change Only**: Report the Transaction Type 83 to Fannie Mae prior to 8 PM eastern standard time on
 *   the 5th Business Day after the Scheduled Payment Calculation Date.
 * - **ARM to Fixed Conversion**: Report a Transaction Type 83 for the conversion by no later than the due date of the
 *   Fannie Mae investor reporting system reports for the reporting period that includes the effective date for the new monthly
 *   payment, but may report the Transaction Type 83 to Fannie Mae when it transmits its first Fannie Mae investor reporting
 *   system reports after it knows the effective date of the conversion and the new converted interest rate and monthly payment.
 * - **Unscheduled Interest Rate/Payment Change**: Use the Transaction Type 83 to report any unscheduled change event for
 *   a loan, including...
 *     - a change to the loan’s P&I due to a principal curtailment made by the borrower, or
 *     - a change to the loan’s interest rate and P&I due to the borrower’s qualifying for an interest rate reduction (such as
         a Timely Payment Reward loan).
 */
export const FannieMaeLar83V1 = z.object({
  version: z.literal('1').describe('version'),
  /**
   * ##### Lender Number
   * Numeric length 9; characters 1 - 9.
   */
  lenderNumber: z.string().describe('string'),
  /**
   * ##### Investor
   * Always `'F'` for Fannie; character 10
   */
  investor: z.literal('F').describe('literal'),
  /**
   * ##### Record Identifier
   * Always `'83'`; characters 11 - 12
   */
  recordIdentifier: z.literal('83').describe('literal'),
  /**
   * ##### Source Code
   * Always `'0'`; character 13
   */
  sourceCode: z.literal('0').describe('literal'),
  /**
   * ##### Fannie Mae Loan Number
   * Numeric length 10; characters 14 - 23
   */
  fannieMaeLoanNumber: z.string().describe('string'),
  /**
   * ##### Effective with Payment Due on
   * MMYY format, length 4; characters 24 - 27
   */
  effectiveWithPaymentDueOn: zodDateOrString.describe('date'),
  /**
   * ##### Index Value (ARM loans only)
   * Alphanumeric 99v9999 format, or blank; e.g. code 6.5% as 065000. Characters 28 - 33
   */
  indexValue: z.string().optional().describe('string'),
  /**
   * ##### New Interest Rate
   * Alphanumeric 99v9999 format, or blank; e.g. code 8.25% as 082500. Characters 34 - 39
   */
  newInterestRate: z.string().optional().describe('string'),
  /**
   * ##### Pass-Through Rate (MBS loans only)
   * Alphanumeric 99v9999 format, or blank; e.g. code 7.25% as 072500. Characters 40 - 45
   */
  passThroughRate: z.string().optional().describe('string'),
  /**
   * ##### Extended Term
   * The number of months being added to the term, e.g. an extra 24 months would be `024`. Alphanumeric length 3; characters 55 - 57
   */
  extendedTerm: z.string().optional().describe('string'),
  /**
   * ##### Converted To Fixed Rate
   * either `'Y'` if yes or blank if no; character 58
   */
  convertedToFixedRate: z.boolean().describe('boolean'),
  /**
   * The report ends with 22 blank characters, 59 - 80
   */
});
export type FannieMaeLar83V1 = z.infer<typeof FannieMaeLar83V1>;

export const FannieMaeLar83 = FannieMaeLar83V1;
export type FannieMaeLar83 = z.infer<typeof FannieMaeLar83>;
// Make `FannieMaeLar83` a discriminated union on 'version' when changes happen
// export const FannieMaeLar83V2 = z.object({
//   version: z.literal('2'),
//   foo: z.string(),
// });
// export const FannieMaeLar83 = z.discriminatedUnion('version', [FannieMaeLar83V1, FannieMaeLar83V02012025]);
